import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const EmployeeRetentionRate = () => {
  // State variables for Retention Rate calculation
  const [employeesStart, setEmployeesStart] = useState(0); // Employees at the start of the period
  const [employeesEnd, setEmployeesEnd] = useState(0); // Employees at the end of the period
  const [employeesLeft, setEmployeesLeft] = useState(0); // Employees who left during the period
  const [retentionRate, setRetentionRate] = useState(0); // Calculated retention rate

  // Event handlers to update state values
  const handleEmployeesStartChange = (e) =>
    setEmployeesStart(parseInt(e.target.value) || 0);
  const handleEmployeesEndChange = (e) =>
    setEmployeesEnd(parseInt(e.target.value) || 0);
  const handleEmployeesLeftChange = (e) =>
    setEmployeesLeft(parseInt(e.target.value) || 0);

  // useEffect hook to calculate retention rate when any input changes
  useEffect(() => {
    if (employeesStart > 0) {
      const rate = ((employeesEnd - employeesLeft) / employeesStart) * 100;
      setRetentionRate(rate);
    } else {
      setRetentionRate(0);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });
  }, [employeesStart, employeesEnd, employeesLeft]);

  return (
    <Layout
      title="Employee Retention Rate Calculator"
      path="employee-retention-rate"
    >
      <section className="section-padding" id="retention-rate-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Retention Rate Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">
                  Employee Retention Rate Calculator
                </h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Employees at Start of Period Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="employeesStart" className="form-label">
                        Employees at Start of Period
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Employees at Start of Period:</strong> The total number of employees at the beginning of the measurement period."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="employeesStart"
                        placeholder="Enter number of employees"
                        value={employeesStart || ""}
                        onChange={handleEmployeesStartChange}
                      />
                    </div>

                    {/* Employees at End of Period Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="employeesEnd" className="form-label">
                        Employees at End of Period
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Employees at End of Period:</strong> The total number of employees at the end of the measurement period."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="employeesEnd"
                        placeholder="Enter number of employees"
                        value={employeesEnd || ""}
                        onChange={handleEmployeesEndChange}
                      />
                    </div>

                    {/* Employees Left During Period Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="employeesLeft" className="form-label">
                        Employees Left During Period
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Employees Left During Period:</strong> The number of employees who left the organization during the measurement period."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="employeesLeft"
                        placeholder="Enter number of employees"
                        value={employeesLeft || ""}
                        onChange={handleEmployeesLeftChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Retention Rate:{" "}
                        <span className="fw-bold ms-3">
                          {retentionRate === 0
                            ? "0.00"
                            : retentionRate.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}{" "}
                          %
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Employee Retention Rate Calculator
                </h3>

                {/* What is an Employee Retention Rate Calculator? */}
                <h4 className="mt-3 mb-3">
                  What is an Employee Retention Rate Calculator?
                </h4>
                <p>
                  An **Employee Retention Rate Calculator** measures the
                  percentage of employees that a company retains over a specific
                  period. It is a vital metric for tracking workforce stability
                  and assessing employee satisfaction.
                </p>

                {/* Why Use an Employee Retention Rate Calculator? */}
                <h4 className="mt-4 mb-3">
                  Why Use an Employee Retention Rate Calculator?
                </h4>
                <p>
                  Understanding your retention rate is crucial for identifying
                  potential issues in employee satisfaction and designing
                  effective talent management strategies. A high retention rate
                  indicates stability, while a low rate may signal
                  dissatisfaction or cultural issues within the organization.
                </p>

                {/* Benefits of Using an Employee Retention Rate Calculator */}
                <h4 className="mt-4 mb-3">
                  Benefits of Using an Employee Retention Rate Calculator
                </h4>
                <ul>
                  <li>
                    <strong>Tracks Workforce Stability:</strong> Provides
                    insights into employee turnover and retention trends.
                  </li>
                  <li>
                    <strong>Identifies Satisfaction Issues:</strong> Helps
                    pinpoint areas where employee satisfaction can be improved.
                  </li>
                  <li>
                    <strong>Supports Talent Management:</strong> Guides HR in
                    designing retention strategies and improving workplace
                    culture.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">
                  Who Should Use an Employee Retention Rate Calculator?
                </h4>
                <p>This calculator is suitable for:</p>
                <ul>
                  <li>
                    <strong>HR Departments:</strong> Helps HR professionals
                    monitor and improve retention strategies.
                  </li>
                  <li>
                    <strong>Corporate Management:</strong> Useful for
                    understanding workforce dynamics and planning talent
                    initiatives.
                  </li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">
                  What is a good employee retention rate?
                </h4>
                <p>
                  A good retention rate varies by industry, but a rate of 90% or
                  higher is generally considered excellent. High retention rates
                  indicate satisfied employees and effective retention policies.
                </p>

                <h4 className="mt-4 mb-3">
                  How can I improve my employee retention rate?
                </h4>
                <p>
                  You can improve your retention rate by fostering a positive
                  work environment, offering competitive benefits, providing
                  career development opportunities, and addressing employee
                  concerns promptly.
                </p>

                <h4 className="mt-4 mb-3">
                  Why is employee retention important?
                </h4>
                <p>
                  Retaining employees reduces turnover costs, ensures workforce
                  stability, and enhances overall productivity. It also helps
                  maintain organizational knowledge and fosters a consistent
                  company culture.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default EmployeeRetentionRate;
